import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { RootStore } from './RootStore';
import axios from 'axios';
import { API } from '../constants';

export interface User {
    id: number;
    username: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    alias?: string;
    role: 'admin' | 'manager' | 'operator';
    is_active: boolean;
    date_joined: string;
    last_login?: string;
}

export interface Driver {
    id: number;
    user_id?: number;
    first_name: string;
    last_name: string;
    phone: string;
    cedula: string;
    license_number?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface UserFormData {
    username: string;
    email: string;
    password: string;
    confirmPassword?: string;
    alias?: string;
    role: 'ADMIN' | 'MANAGER' | 'DISPATCHER' | 'DRIVER' | 'CLIENT';
    driver_id?: number;
}

export class UserStore {
    users: User[] = [];
    drivers: Driver[] = [];
    isLoading = false;
    isSubmitting = false;
    error: string | null = null;

    private rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            users: observable,
            drivers: observable,
            isLoading: observable,
            isSubmitting: observable,
            error: observable,
            createUser: action,
            loadUsers: action,
            loadDrivers: action,
            clearError: action,
            setError: action,
            availableDrivers: computed
        });
    }

    get availableDrivers() {

        return this.drivers.filter(driver => !driver.user_id && driver.is_active);
    }

    setError = (error: string) => {
        this.error = error;
    };

    clearError = () => {
        this.error = null;
    };

    loadUsers = async () => {
        this.isLoading = true;
        this.error = null;

        try {
            const response = await axios.get(`${API.BASE_URL.DEVELOPMENT}/users/`);
            runInAction(() => {
                this.users = response.data.results || response.data || [];
                this.isLoading = false;
            });
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.detail || 'Error al cargar usuarios';
                this.isLoading = false;
            });
        }
    };

    loadDrivers = async () => {
        this.isLoading = true;
        this.error = null;

        try {
            const response = await axios.get(`${API.BASE_URL.DEVELOPMENT}/drivers/`);
            runInAction(() => {
                this.drivers = response.data.results || response.data || [];
                this.isLoading = false;
            });
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.detail || 'Error al cargar conductores';
                this.isLoading = false;
            });
        }
    };

    createUser = async (userData: UserFormData): Promise<boolean> => {
        this.isSubmitting = true;
        this.error = null;

        try {
            const payload = {
                username: userData.username,
                email: userData.email,
                password: userData.password,
                password2: userData.password,
                alias: userData.alias,
                role: userData.role,
                ...(userData.driver_id && { driver_id: userData.driver_id })
            };

            const response = await axios.post(`${API.BASE_URL.DEVELOPMENT}/auth/register/`, payload);

            runInAction(() => {

                this.users.unshift(response.data);


                if (userData.driver_id) {
                    const driverIndex = this.drivers.findIndex(d => d.id === userData.driver_id);
                    if (driverIndex >= 0) {
                        this.drivers[driverIndex].user_id = response.data.id;
                    }
                }

                this.isSubmitting = false;
            });

            return true;
        } catch (error: any) {
            runInAction(() => {
                if (error.response?.data) {

                    if (error.response.data.username) {
                        this.error = `Usuario: ${error.response.data.username[0]}`;
                    } else if (error.response.data.password) {
                        this.error = `Contraseña: ${error.response.data.password[0]}`;
                    } else if (error.response.data.detail) {
                        this.error = error.response.data.detail;
                    } else {
                        this.error = 'Error al crear el usuario';
                    }
                } else {
                    this.error = 'Error de conexión. Intenta nuevamente.';
                }
                this.isSubmitting = false;
            });
            return false;
        }
    };


    validateUserForm = (formData: UserFormData): { isValid: boolean; errors: Record<string, string> } => {
        const errors: Record<string, string> = {};


        if (!formData.username.trim()) {
            errors.username = 'El nombre de usuario es requerido';
        } else if (formData.username.length < 3) {
            errors.username = 'El nombre de usuario debe tener al menos 3 caracteres';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
            errors.username = 'El nombre de usuario solo puede contener letras, números y guiones bajos';
        }


        if (!formData.email.trim()) {
            errors.email = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'El formato del email no es válido';
        }


        if (!formData.password) {
            errors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 6) {
            errors.password = 'La contraseña debe tener al menos 6 caracteres';
        }


        if (formData.confirmPassword !== undefined) {
            if (formData.password !== formData.confirmPassword) {
                errors.confirmPassword = 'Las contraseñas no coinciden';
            }
        }


        if (!formData.role) {
            errors.role = 'El rol es requerido';
        }


        if (formData.role === 'DRIVER' && !formData.driver_id) {
            errors.driver_id = 'Debe seleccionar un conductor para el rol de DRIVER';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };
}
